'use client';

import type { RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import { CalendarIcon, XCircle } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import * as React from 'react';
import type { DateRange } from '@daypicker/react';
import { ar } from '@daypicker/react/locale';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { formatDate } from '@/lib/format';

interface DataTableDateTimeRangeFilterProps<TData extends RowData> {
  column: Column<TData, unknown>;
  title?: string;
}

function parseRange(value: unknown): {
  from: Date | undefined;
  to: Date | undefined;
} {
  if (!Array.isArray(value)) return { from: undefined, to: undefined };
  const fromTs = value[0];
  const toTs = value[1];
  const from =
    typeof fromTs === 'number'
      ? new Date(fromTs)
      : (undefined as Date | undefined);
  const to =
    typeof toTs === 'number' ? new Date(toTs) : (undefined as Date | undefined);
  return {
    from: from && !Number.isNaN(from.getTime()) ? from : undefined,
    to: to && !Number.isNaN(to.getTime()) ? to : undefined
  };
}

function formatTimeInput(d: Date | undefined): string {
  if (!d) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function applyTimeToDate(date: Date, time: string): Date {
  const [hStr = '0', mStr = '0', sStr = '0'] = time.split(':');
  const next = new Date(date);
  next.setHours(Number(hStr) || 0, Number(mStr) || 0, Number(sStr) || 0, 0);
  return next;
}

export function DataTableDateTimeRangeFilter<TData extends RowData>({
  column,
  title
}: DataTableDateTimeRangeFilterProps<TData>) {
  const locale = useLocale();
  const t = useTranslations('common');
  const dayPickerLocale = locale === 'ar' ? ar : undefined;
  const calendarDir = locale === 'ar' ? 'rtl' : 'ltr';

  const filterValue = column.getFilterValue();
  const { from, to } = React.useMemo(
    () => parseRange(filterValue),
    [filterValue]
  );

  const setRange = React.useCallback(
    (next: { from?: Date; to?: Date }) => {
      const fromTs = next.from?.getTime();
      const toTs = next.to?.getTime();
      column.setFilterValue(fromTs || toTs ? [fromTs, toTs] : undefined);
    },
    [column]
  );

  const onCalendarSelect = React.useCallback(
    (range: DateRange | undefined) => {
      if (!range) {
        column.setFilterValue(undefined);
        return;
      }
      // Preserve the time component if a date already had one
      const nextFrom =
        range.from && from
          ? applyTimeToDate(range.from, formatTimeInput(from))
          : range.from;
      const nextTo =
        range.to && to
          ? applyTimeToDate(range.to, formatTimeInput(to))
          : range.to;
      setRange({ from: nextFrom, to: nextTo });
    },
    [from, to, setRange, column]
  );

  const onTimeChange = React.useCallback(
    (which: 'from' | 'to', time: string) => {
      const base = which === 'from' ? from : to;
      if (!base) return;
      const next = applyTimeToDate(base, time);
      setRange(which === 'from' ? { from: next, to } : { from, to: next });
    },
    [from, to, setRange]
  );

  const onReset = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      column.setFilterValue(undefined);
    },
    [column]
  );

  const hasValue = !!(from || to);

  const label = React.useMemo(() => {
    const formatOne = (d: Date) =>
      `${formatDate(d, {}, locale)} ${formatTimeInput(d).slice(0, 5)}`;

    let dateText: string | null = null;
    if (from && to) dateText = `${formatOne(from)} → ${formatOne(to)}`;
    else if (from) dateText = formatOne(from);
    else if (to) dateText = formatOne(to);

    return (
      <span className='flex items-center gap-2'>
        <span>{title}</span>
        {dateText && (
          <>
            <Separator
              orientation='vertical'
              className='mx-0.5 data-[orientation=vertical]:h-4'
            />
            <span>{dateText}</span>
          </>
        )}
      </span>
    );
  }, [from, to, locale, title]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='border-dashed'>
          {hasValue ? (
            <div
              role='button'
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onReset}
              className='focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-1 focus-visible:outline-none'
            >
              <XCircle />
            </div>
          ) : (
            <CalendarIcon />
          )}
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          autoFocus
          mode='range'
          locale={dayPickerLocale}
          dir={calendarDir}
          selected={{ from, to }}
          onSelect={onCalendarSelect}
        />
        <Separator />
        <div className='grid grid-cols-2 gap-3 p-3'>
          <div className='flex flex-col gap-1.5'>
            <Label className='text-xs font-medium'>
              {t('dateFilter.fromTime')}
            </Label>
            <Input
              type='time'
              step='1'
              value={formatTimeInput(from)}
              disabled={!from}
              onChange={(e) => onTimeChange('from', e.target.value)}
              className='h-8'
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label className='text-xs font-medium'>
              {t('dateFilter.toTime')}
            </Label>
            <Input
              type='time'
              step='1'
              value={formatTimeInput(to)}
              disabled={!to}
              onChange={(e) => onTimeChange('to', e.target.value)}
              className='h-8'
            />
          </div>
        </div>
        <Separator />
        <div className='flex justify-end p-2'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => column.setFilterValue(undefined)}
          >
            {t('dateFilter.reset')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
